import React, { useCallback, useEffect, useRef } from 'react';
import { Keyboard, X } from 'lucide-react';
import TimesheetEditManager from '../../managers/TimesheetEditManager';
import {
  PayrollEditEntryKind,
  PayrollEntryBody,
  PayrollEntryResult,
  TIMESHEET_EDIT_CONFLICT_MESSAGE,
} from '../../interfaces/TimesheetEditInterface';
import { ENTRY_KINDS, EditorKind } from './entryKinds';
import './PayrollSubmitWizard.css';
import './PayrollEntryModal.css';

/**
 * Payroll editor (M2-B) — the shared frame all six entry modals render in.
 * Owns the accepted-design keyboard contract (closed decisions, ITERATION-2):
 *
 *   Enter        save + reopen a fresh modal, same kind, same day (carry prefills)
 *   Shift+Enter  save + close
 *   Esc          cancel
 *   Tab          natural order: in → out → save
 *
 * The footer names both paths — "Save" and "Save + add another" — so the loop
 * is discoverable without knowing the shortcut. The echo line lives with the
 * modal body (each kind supplies its own parse echo), the kbd hints here.
 *
 * The rapid-entry loop only exists when you are ADDING. Editing one existing
 * entry offers Cancel + Save, and Enter saves and closes — the add-another path
 * would otherwise drop an unasked-for blank form on top of the week you were
 * correcting.
 */

export interface EntryModalShellProps {
  kind: EditorKind;
  mode: 'add' | 'edit';
  /** "Wed Aug 5 · Maria Fuentes". */
  subtitle: string;
  banner?: React.ReactNode;
  /** Kind-specific parse echo parts; the shell appends the kbd hints. */
  echo?: React.ReactNode;
  /**
   * Replaces the shell's default kbd hints. The one caller is the task modal:
   * while its picker holds the keyboard, "Enter save + next" is a lie — the
   * echo line is the only place that can say WHICH Enter you are on. Omitted
   * (every other modal) leaves the shipped hints byte-identical.
   */
  keysHint?: React.ReactNode;
  /** Extra class on `.pe-entry-modal` (the task kind widens to 680px). */
  modalClassName?: string;
  saving: boolean;
  /** `close=false` → save + add another (the Enter loop); `true` → save + close. */
  onSave: (close: boolean) => void;
  onCancel: () => void;
  children: React.ReactNode;
}

export const EntryModalShell: React.FC<EntryModalShellProps> = ({
  kind, mode, subtitle, banner, echo, keysHint,
  modalClassName, saving, onSave, onCancel, children,
}) => {
  const K = ENTRY_KINDS[kind];
  const Icon = K.Icon;
  const isEdit = mode === 'edit';
  const wrapRef = useRef<HTMLDivElement>(null);

  // Esc/Enter ride the wrapper's bubbled keydown, so the wrapper has to be able
  // to hold focus itself — otherwise the contract dies the moment focus leaves
  // the fields (a scrim click, a mouse-up on the header).
  useEffect(() => {
    if (!wrapRef.current?.contains(document.activeElement)) wrapRef.current?.focus();
  }, []);

  // A child that owns a key stops its propagation (the task picker's list does
  // this for Enter/arrows, and for Esc ONLY while its search box has a term —
  // an empty search lets Esc through so it still cancels the modal).
  const onKeyDown = useCallback((ev: React.KeyboardEvent) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      if (!saving) onSave(isEdit ? true : ev.shiftKey);
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      if (!saving) onCancel();
    }
  }, [saving, isEdit, onSave, onCancel]);

  /** Scrim click cancels; a click that started inside the card never does. */
  const onScrimMouseDown = useCallback((ev: React.MouseEvent) => {
    if (ev.target === ev.currentTarget && !saving) onCancel();
  }, [saving, onCancel]);

  return (
    <div
      ref={wrapRef}
      className="pe-mwrap open"
      role="dialog"
      aria-modal="true"
      aria-label={`${isEdit ? 'Edit' : 'Add'} ${K.noun}`}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onMouseDown={onScrimMouseDown}
    >
      <div className={`pe-modal pe-entry-modal${modalClassName ? ` ${modalClassName}` : ''}`}>
        <div className="pe-mhead">
          <span className="mi" style={{ color: K.color, background: K.bg }}><Icon /></span>
          <div className="grow">
            <h3>{isEdit ? 'Edit' : 'Add'} {K.noun}</h3>
            <div className="sub">{subtitle}</div>
          </div>
          <button className="pe-iact" onClick={onCancel} disabled={saving} title="Close" type="button">
            <X />
          </button>
        </div>
        <div className="pe-mbody">
          <div className="pe-msect">
            {banner}
            {children}
            <div className="pe-echo">
              <Keyboard className="pe-echo-ic" />
              {echo}
              {keysHint ?? (
                <>
                  {isEdit ? (
                    <>
                      <span className="kbd">Enter</span> save
                    </>
                  ) : (
                    <>
                      <span className="kbd">Enter</span> save + next
                      <span className="dot">·</span>
                      <span className="kbd">Shift ⏎</span> save + close
                    </>
                  )}
                  <span className="dot">·</span>
                  <span className="kbd">Esc</span> cancel
                </>
              )}
            </div>
          </div>
        </div>
        <div className="pe-mfoot">
          <button className="pe-btn" onClick={onCancel} disabled={saving} type="button">Cancel</button>
          <span className="grow" />
          <button
            className={`pe-btn${isEdit ? ' primary' : ''}`}
            onClick={() => onSave(true)}
            disabled={saving}
            type="button"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {!isEdit && (
            <button className="pe-btn primary" onClick={() => onSave(false)} disabled={saving} type="button">
              {saving ? 'Saving…' : 'Save + add another'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Shared save plumbing ─────────────────────────────────────────────────────

export type EntrySaveOutcome =
  | { ok: true; result: PayrollEntryResult }
  | { ok: false; status: number | null; message: string; conflict: boolean; code?: string };

/**
 * Create-or-update through the manager, mapping the designed error states:
 * a 409 is a conflict (the period was submitted mid-edit — the PAGE owns the
 * recovery: reload state, show the already-submitted view), everything else
 * surfaces as an in-modal banner message.
 */
export async function performEntrySave(
  kind: PayrollEditEntryKind,
  entityID: number | null,
  body: PayrollEntryBody
): Promise<EntrySaveOutcome> {
  try {
    const result = entityID == null
      ? await TimesheetEditManager.createEntry(kind, body)
      : await TimesheetEditManager.updateEntry(kind, entityID, body);
    return { ok: true, result };
  } catch (err: unknown) {
    const e = err as { response?: { status?: number; data?: { message?: string; code?: string } } };
    const status = e?.response?.status ?? null;
    const message = e?.response?.data?.message
      ?? (status === 409 ? TIMESHEET_EDIT_CONFLICT_MESSAGE : 'Could not save the entry. Please try again.');
    const code = typeof e?.response?.data?.code === 'string' ? e.response.data.code : undefined;
    return { ok: false, status, message, conflict: status === 409, ...(code ? { code } : {}) };
  }
}

/**
 * The punch modals' clock error line, under the In / Out fields. Always mounted at a
 * fixed height and toggled with `visibility`, so the cross-midnight refusal (client
 * check or the API's PUNCH_CROSSES_MIDNIGHT 400) never resizes the modal.
 */
export const PunchErrorSlot: React.FC<{ message: string | null }> = ({ message }) => (
  <div
    className="pe-punch-err"
    role="alert"
    aria-hidden={!message}
    style={{ visibility: message ? 'visible' : 'hidden' }}
  >
    {message ?? ''}
  </div>
);

/** Standard danger banner for the modals (task modal builds its own budget one). */
export const EntryErrorBanner: React.FC<{ title: string; body?: React.ReactNode }> = ({ title, body }) => (
  <div className="pe-ban danger">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    <div className="grow">
      <b>{title}</b>
      {body}
    </div>
  </div>
);
